import { useId, useRef } from 'react';
import type { ReactNode, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useModal } from './modal';
import { useDensity } from './layout/Density';
import type { DensityLevel } from './layout/Density';

/** The controls Tab visits inside `root`, in document order (DS-11a2: it lives with `useModal`, in modal.ts). */
export { tabbables } from './modal';

/**
 * 🔴 THE DIALOG — design-system plan DS-8 (5.1; F39, F496, F544, F596). The one modal surface every question is
 * drawn in: `ConfirmDialog` and `PromptDialog` are this with their buttons and their field.
 *
 * On the native `<dialog>`, opened with `showModal()`, so the browser puts it in the top layer, draws `::backdrop`
 * and makes the rest of the page inert. What this adds, because the browser does not do all of it everywhere:
 *
 * - FOCUS GOES IN: to `initialFocus` when the caller names it, else the first field, else the first button.
 * - TAB STAYS IN: Tab from the last control goes to the first, Shift+Tab from the first to the last. The browser's own
 *   modal lets Tab leave for the address bar; a person tabbing through a till prompt must not lose it.
 * - ESCAPE CANCELS: the same `onCancel` as the Cancel button — never a quiet "yes". It stops there, so a drawer or the
 *   palette listening on the document does not close too.
 * - FOCUS COMES BACK to the element that had it when the dialog opened (the "Void" button), once the dialog has gone.
 * - THE PAGE IS INERT: every other child of `<body>` carries `inert` while it is open, and loses it after — only the
 *   ones this dialog quieted, so a dialog opened over another leaves the first one's work alone.
 *
 * The title labels it (`aria-labelledby`), the body describes it (`aria-describedby`). It is drawn into `<body>`
 * through a portal, which is what lets the rest of the page be made inert without the dialog inside it. At ≤480px it
 * is a bottom sheet (components.css, the dialog layer).
 *
 * DS-11a2: the five behaviours above live in `useModal` (`modal.ts`), shared with the command palette; this is its
 * first user, unchanged in behaviour (dialog.spec.tsx).
 *
 * The caller mounts it while the question is open and unmounts it when answered; `onSubmit` wraps the fields and the
 * actions in a form, so Enter in a field is the confirm button.
 */
export interface DialogProps {
  /** Verb + object: "Void invoice INV-0042?" */
  title: string;
  /** The consequence, in a sentence. */
  body?: ReactNode;
  /** The fields, between the body and the actions. */
  children?: ReactNode;
  /** The buttons: the way back first, the verb last. */
  actions: ReactNode;
  /** Escape, and the way back. */
  onCancel: () => void;
  /** Given, the fields and actions are a form and Enter in a field submits it. */
  onSubmit?: () => void;
  /** `alertdialog` for a question that interrupts (a confirm); `dialog` for one that asks for input. */
  role?: 'dialog' | 'alertdialog';
  /** The control focus goes to first. */
  initialFocus?: RefObject<HTMLElement | null>;
  /**
   * The density it is drawn at (DS-9a): the level of the subtree that asked. Drawn into `<body>`, the dialog would
   * otherwise take the page's — comfortable on a touch screen — however the screen that opened it is set. Without
   * it, the level of the `<Density>` the dialog is rendered in, if any (context reaches through the portal).
   */
  density?: DensityLevel;
  className?: string;
}

export function Dialog({ title, body, children, actions, onCancel, onSubmit, role = 'dialog', initialFocus, density, className }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const around = useDensity();
  const titleId = useId();
  const bodyId = useId();
  useModal(ref, { onCancel, initialFocus });

  const content = (
    <>
      {children}
      <div className="wb-dialog__actions">{actions}</div>
    </>
  );
  return createPortal(
    <dialog
      ref={ref}
      className={['wb-dialog', className ?? ''].filter(Boolean).join(' ')}
      data-density={density ?? around}
      role={role === 'alertdialog' ? 'alertdialog' : undefined}
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={body ? bodyId : undefined}
    >
      <h2 className="wb-dialog__title" id={titleId}>
        {title}
      </h2>
      {body ? (
        <div className="wb-dialog__body" id={bodyId}>
          {body}
        </div>
      ) : null}
      {onSubmit ? (
        <form
          className="wb-dialog__form"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          {content}
        </form>
      ) : (
        content
      )}
    </dialog>,
    document.body,
  );
}
